// Standalone pricing page

import React from 'react';
import { Link } from 'react-router';
import {
  HandCoins,
  LibraryBig,
  MonitorX,
  Music2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UsersRound,
  Heart,
  type LucideIcon,
} from 'lucide-react';
import UspSection from './UspSection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { getColorStop, type PlayerColorFamily } from '@/styles/playerColors';
import SEO from '@/seo/SEO';
import PricingTable from '@/components/products/PricingTable';
import { BRAND_LINKS } from '@airhead-games/shared/configs';
import type { FAQItem } from '@/types/faq';

interface PricingFeature {
  icon: LucideIcon;
  accentFamily: PlayerColorFamily;
  title: string;
  description: string;
}

const sharedPlanFeatures: PricingFeature[] = [
  {
    icon: LibraryBig,
    accentFamily: 'orange',
    title: 'Full game library',
    description:
      'Play any game in the library without restrictions. New games are added regularly.',
  },
  {
    icon: UsersRound,
    accentFamily: 'yellow',
    title: 'Free player access',
    description:
      'Players join your games from anywhere for free. No downloads or accounts required.',
  },
  {
    icon: MonitorX,
    accentFamily: 'green',
    title: 'Ad-free gameplay',
    description: 'No ads or in-app purchases. Play without interruptions.',
  },
  {
    icon: ShieldCheck,
    accentFamily: 'teal',
    title: 'Moderation tools',
    description:
      'Access controls, profanity filtering, and other tools to protect your game environment.',
  },
  {
    icon: HandCoins,
    accentFamily: 'cyan',
    title: 'Predictable pricing',
    description:
      'Every game offers 10-20 minutes of fun for one Loon. Great value without surprises.',
  },
  {
    icon: Sparkles,
    accentFamily: 'blue',
    title: 'Fresh content',
    description:
      'New content generated every time you play. Always fresh and tailored to your group.',
  },
  {
    icon: Heart,
    accentFamily: 'violet',
    title: 'Premium support',
    description:
      '<24 hour response time for support requests via email or Discord.',
  },
  {
    icon: Music2,
    accentFamily: 'fuchsia',
    title: 'Stream-safe music',
    description:
      'Every track is original and royalty-free for livestreams, content clips, and recordings.',
  },
  {
    icon: SlidersHorizontal,
    accentFamily: 'rose',
    title: 'Totally yours',
    description:
      'Adjust personality, tone, and language preferences to shape the exact vibe you want.',
  },
];

const pricingFaqItems: FAQItem[] = [
  {
    question: 'What is a Loon?',
    answer:
      'A Loon is a game credit for Airhead Games. We believe game credits are the fairest exchange of value: costs are predictably tied to the games you play... and same for us!',
    schemaAnswer:
      'A Loon is a game credit for Airhead Games. We believe game credits are the fairest exchange of value: costs are predictably tied to the games you play, and same for us.',
  },
  {
    question: 'Do Loons expire?',
    answer: 'No. Your Loon balance stays in your account until you use it.',
    schemaAnswer:
      'No. Your Loon balance stays in your account until you use it.',
  },
  {
    question: 'Does every player need to buy Loons?',
    answer: 'No. Only the host needs Loons. Players join games for free.',
    schemaAnswer: 'No. Only the host needs Loons. Players join games for free.',
  },
  {
    question: 'Can I exchange my Loons?',
    answer:
      'No. Loons are not exchangeable or redeemable for cash. Refunds may be granted at our discretion in some situations. See our Terms & Conditions for details.',
    schemaAnswer:
      'No. Loons are not exchangeable or redeemable for cash. Refunds may be granted at our discretion in some situations. See our Terms and Conditions for details.',
  },
  {
    question: 'Is checkout secure?',
    answer:
      'Yes. Checkout is powered by Stripe, including their secure payment flow and tax handling where required.',
    schemaAnswer:
      'Yes. Checkout is powered by Stripe, including their secure payment flow and tax handling where required.',
  },
  {
    question: 'Is Airhead Games legit?',
    schemaAnswer: `Yes! Airhead Games is recognized by IGDB (The Internet Game Database), has an official Twitch category (${BRAND_LINKS.twitchDirectory}), and uses Stripe and Twitch for secure payment and authentication integrations. Check out our gameplay clips at airheadgames.com/discover/clips.`,
    answer: (
      <>
        Yes! Airhead Games is recognized by{' '}
        <a
          href="https://www.igdb.com/games/airhead-games"
          target="_blank"
          rel="noopener noreferrer"
        >
          IGDB
        </a>{' '}
        (The Internet Game Database), has an official{' '}
        <a
          href={BRAND_LINKS.twitchDirectory}
          target="_blank"
          rel="noopener noreferrer"
        >
          Twitch category
        </a>
        , and uses Stripe and Twitch for secure payment and authentication
        integrations. Check out <Link to="/discover/clips">Clips</Link> to see
        our games in action!
      </>
    ),
  },
];

const Pricing: React.FC = () => {
  return (
    <div className="min-h-svh bg-background text-foreground">
      <SEO faqItems={pricingFaqItems} />
      <main className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-6 pb-12 pt-28 md:gap-16 md:px-8 md:pb-16 md:pt-32 lg:px-12">
        <section className="mx-auto flex w-full max-w-4xl flex-col gap-4 text-center">
          <h1 className="font-heading text-4xl leading-tight tracking-tight md:text-5xl">
            Simple pricing for streamers
          </h1>
          <p className="text-base leading-6 text-muted-foreground md:text-lg">
            Use Loons only when you host games: predictable cost per session,
            credits that never expire, and affordable fun your chat can join for
            free.
          </p>
        </section>

        <section className="flex w-full flex-col gap-6">
          <PricingTable analyticsSurface="pricing_page" />
        </section>

        <div className="flex flex-col gap-10 md:gap-16">
          <section className="flex flex-col gap-6">
            <div className="mx-auto flex max-w-3xl flex-col gap-3 text-center">
              <h2 className="font-heading text-2xl leading-tight tracking-tight md:text-3xl">
                Included in every Loon pack
              </h2>
              <p className="text-sm leading-6 text-muted-foreground md:text-base">
                The full Airhead Games platform comes with every purchase
              </p>
            </div>
            <UspSection
              usps={sharedPlanFeatures.map((feature) => ({
                icon: feature.icon,
                bigText: feature.title,
                smallText: feature.description,
                accentFamily: feature.accentFamily,
                iconColor: getColorStop(feature.accentFamily, '400'),
              }))}
              columns={3}
              mobileColumns={1}
              fullWidth
            />
          </section>

          <section className="mx-auto flex w-full max-w-4xl flex-col gap-6">
            <div className="mx-auto flex max-w-3xl flex-col gap-3 text-center">
              <h2 className="font-heading text-2xl leading-tight tracking-tight md:text-3xl">
                Pricing FAQs
              </h2>
              <p className="text-sm leading-6 text-muted-foreground md:text-base">
                Quick answers before you choose a pack
              </p>
            </div>
            <Accordion
              type="single"
              collapsible
              className="w-full border-y border-border/80"
            >
              {pricingFaqItems.map((item, index) => (
                <AccordionItem key={item.question} value={`item-${index}`}>
                  <AccordionTrigger className="font-heading text-lg leading-snug font-medium hover:text-primary">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="text-base leading-7 text-muted-foreground [&_a]:cursor-pointer [&_a]:text-primary [&_a]:underline-offset-4 [&_a:hover]:underline">
                      {item.answer}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Pricing;
